import { useAuth } from "@/contexts/AuthContext";
import {
  ChatList,
  ChatView,
  NewChatDialog,
  useAdminChats,
  useChatMessages,
} from "@/features/chat";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { MessageCircle } from "lucide-react";

export const Route = createFileRoute("/dashboard/admin/chat/$chatId/")({
  component: ChatDetailPage,
});

function ChatDetailPage() {
  const { chatId } = Route.useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  const {
    chats,
    loading: chatsLoading,
    totalUnreadCount,
    createDirectChat,
    createGroupChat,
  } = useAdminChats(userProfile?.uid);

  const {
    messages,
    chat,
    loading: messagesLoading,
    sending,
    sendMessage,
  } = useChatMessages(chatId, userProfile?.uid);

  const handleChatCreated = (newChatId: string) => {
    navigate({ to: "/dashboard/admin/chat/$chatId", params: { chatId: newChatId } });
  };

  const handleBack = () => {
    navigate({ to: "/dashboard/admin/chat" });
  };

  if (!userProfile) {
    return null;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 border-b bg-white px-4 py-3 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MessageCircle className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold">Portal Chat</h1>
              <p className="text-sm text-gray-600">
                {totalUnreadCount > 0
                  ? `${totalUnreadCount} unread message${totalUnreadCount !== 1 ? "s" : ""}`
                  : "Chat with your team"}
              </p>
            </div>
          </div>
          <NewChatDialog
            currentUserId={userProfile.uid}
            currentUserName={userProfile.name}
            onCreateDirectChat={createDirectChat}
            onCreateGroupChat={createGroupChat}
            onChatCreated={handleChatCreated}
          />
        </div>
      </div>

      {/* Chat Layout */}
      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-4 overflow-hidden p-4 sm:p-6">
        {/* Chat List - Hidden on mobile when viewing a chat */}
        <div className="hidden w-80 shrink-0 overflow-hidden rounded-lg border bg-white shadow-sm md:block">
          <ChatList
            chats={chats}
            currentUserId={userProfile.uid}
            selectedChatId={chatId}
            loading={chatsLoading}
          />
        </div>

        {/* Chat View */}
        <div className="flex-1 overflow-hidden">
          {messagesLoading ? (
            <div className="flex h-full items-center justify-center rounded-lg border bg-white shadow-sm">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : chat ? (
            <ChatView
              chat={chat}
              messages={messages}
              currentUserId={userProfile.uid}
              currentUserName={userProfile.name}
              sending={sending}
              onSendMessage={sendMessage}
              onBack={handleBack}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-lg border bg-white shadow-sm">
              <div className="text-center text-gray-500">
                <MessageCircle className="mx-auto mb-4 h-16 w-16 text-gray-300" />
                <h3 className="mb-2 text-lg font-medium">Chat not found</h3>
                <p className="text-sm">This conversation may have been deleted</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
